import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaMoneyBillWave } from 'react-icons/fa';

import { moisCourant } from '../../components/Utils/mois';
import SelecteurMois from '../../components/Utils/SelecteurMois';
import { apiService } from '../../components/Config/api.service';
import SelecteurPieces, { verifierPieces } from './SelecteurPieces';

/**
 * Dépôt d'une avance sur salaire par son titulaire (T3 de YRH-SHARED-005,
 * `Yros/Kavero#136`).
 *
 * Deux champs, et pas un de plus : un montant et le mois sur lequel la
 * retenue sera appliquée. Le salarié ne choisit ni le bénéficiaire ni l'état —
 * c'est le compte connecté qui désigne l'employé, côté Odoo.
 *
 * Aucune règle métier ici. Le plafond, l'unicité de l'avance en cours et le
 * verrouillage du mois de paie vivent dans Odoo (`Yros/Kavero#134`), et leurs
 * refus s'affichent mot pour mot : les reformuler ferait diverger deux
 * vérités. L'écran ne vérifie que ce qu'il peut voir — un formulaire
 * incomplet, un montant qui n'est pas un nombre strictement positif.
 *
 * Assemblage repris de `DemandeConge`, son frère de destination : mêmes
 * classes, même bouton, même conduite du succès et de l'erreur.
 */

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

function DemandeAvance({ onDepot }) {
  const { t } = useTranslation();
  const [montant, setMontant] = useState('');
  const [mois, setMois] = useState('');
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [pieces, setPieces] = useState([]);

  const soumettre = async (event) => {
    event.preventDefault();
    setErreur(null);
    setSucces(null);

    if (!montant || !mois) {
      setErreur(t('demandeAvance.champsManquants'));
      return;
    }
    // `Number` plutôt que `parseFloat` : « 50 000 » ou « 12abc » doivent être
    // refusés ici, pas partir chez Odoo pour en revenir en 409.
    const montantNombre = Number(montant);
    if (!Number.isFinite(montantNombre) || montantNombre <= 0) {
      setErreur(t('demandeAvance.montantInvalide'));
      return;
    }
    // Comparaison de chaînes « AAAA-MM » : elles s'ordonnent comme les mois.
    // Odoo refuse aussi, et reste juge du mois clôturé que le portail ignore.
    if (mois < moisCourant()) {
      setErreur(t('demandeAvance.moisPasse'));
      return;
    }

    const refusPieces = verifierPieces(pieces, t);
    if (refusPieces) {
      setErreur(refusPieces);
      return;
    }

    setEnvoi(true);
    try {
      const demande = {
        type: 'avance',
        contenu: { montant: montantNombre, mois_retenue: mois },
      };
      // Sans pièce, l'appel est celui d'avant : un seul argument.
      const reponse = pieces.length
        ? await apiService.creerDemandeRh(demande, pieces)
        : await apiService.creerDemandeRh(demande);
      if (reponse && reponse.ok) {
        setSucces(t('demandeAvance.succes'));
        setMontant('');
        setMois('');
        setPieces([]);
        // Le parent recharge la liste : sans ça, le message de succès serait
        // la seule trace de ce qui vient de se passer.
        if (onDepot) onDepot();
      } else {
        setErreur(reponse?.message || t('demandeAvance.erreurGenerique'));
      }
    } catch (e) {
      // Un refus métier remonte en 409, donc par la branche d'erreur d'axios :
      // c'est là que vit le message d'Odoo, et il doit être affiché tel quel.
      setErreur(e?.response?.data?.message || t('demandeAvance.erreurGenerique'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border border-gray-100 space-y-4">
      <div className="flex items-center gap-2">
        <FaMoneyBillWave className="text-[--color-primary]" />
        <h2 className="text-base sm:text-lg font-semibold">{t('demandeAvance.titre')}</h2>
      </div>

      <form data-testid="avance-formulaire" onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm font-medium">
            {t('demandeAvance.montant')}
            <input
              type="number"
              inputMode="numeric"
              min="1"
              step="any"
              data-testid="avance-montant"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block text-sm font-medium">
            {t('demandeAvance.mois')}
            {/* « AAAA-MM » est la forme que le contrat attend. Safari et Firefox
                de bureau n'ont pas `type="month"` : le sélecteur y bascule sur
                deux listes qui composent la même valeur (Yros/Kavero#426). */}
            <SelecteurMois
              testId="avance-mois"
              min={moisCourant()}
              value={mois}
              onChange={setMois}
              className={CLASSE_CHAMP}
            />
          </label>
        </div>

        <SelecteurPieces idPrefix="avance" fichiers={pieces} onChange={setPieces} />

        {erreur && (
          <p
            data-testid="avance-erreur"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {erreur}
          </p>
        )}
        {succes && (
          <p
            data-testid="avance-succes"
            className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
          >
            {succes}
          </p>
        )}

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            data-testid="avance-soumettre"
            disabled={envoi}
            className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FaPaperPlane size={14} />
            {envoi ? t('demandeAvance.envoi') : t('demandeAvance.envoyer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DemandeAvance;
